import React, { useMemo } from 'react'
import { AgGridReact } from 'ag-grid-react'
import { AllCommunityModule, ModuleRegistry, themeQuartz } from 'ag-grid-community'
import CustomHeader from './CustomHeader'
import { hideGridTooltip, showGridTooltip } from './agGridTooltip'

ModuleRegistry.registerModules([AllCommunityModule])

const appTheme = themeQuartz.withParams({
  fontFamily: 'Inter, sans-serif',
  fontSize: 12,
  headerFontSize: 13,
  headerFontWeight: 500,
  headerTextColor: '#343A40',
  headerBackgroundColor: '#F4F5F7',
  foregroundColor: '#343A40',
  accentColor: 'var(--primary)',
  borderColor: '#DEE2E6',
  rowHoverColor: 'rgba(0, 0, 0, 0.03)',
  headerHeight: 42,
  rowHeight: 40,
  cellHorizontalPadding: 10,
  wrapperBorder: false,
  wrapperBorderRadius: 6,
})

export function TruncatedText(params) {
  const raw = params.valueFormatted ?? params.value
  if (raw === undefined || raw === null || raw === '') return null
  if (typeof raw === 'object') return null
  const value = String(raw)
  return (
    <span
      className="ag-truncated-text"
      onMouseEnter={event => showGridTooltip(value, event.currentTarget)}
      onMouseLeave={hideGridTooltip}
    >
      {value}
    </span>
  )
}

/** Hover tooltip for frame names (and other truncated labels) in custom cell renderers. */
export function GridTooltipLabel({ text, className = '', children }) {
  const tip = text == null ? '' : String(text).trim()
  if (!tip) {
    return <span className={className}>{children}</span>
  }
  return (
    <span
      className={className}
      onMouseEnter={event => showGridTooltip(tip, event.currentTarget)}
      onMouseLeave={hideGridTooltip}
    >
      {children ?? tip}
    </span>
  )
}

const HEADER_HEIGHT = 42

export default function AgGridTable({
  rowData,
  columnDefs,
  defaultColDef,
  className = '',
  height = 560,
  getRowClass,
  getRowId,
  onGridReady,
  pagination = false,
  paginationPageSize = 10,
  headerHeight = HEADER_HEIGHT,
  loading = false,
  ...rest
}) {
  const mergedDefaultColDef = useMemo(() => ({
    resizable: false,
    filter: false,
    sortable: false,
    flex: 1,
    minWidth: 90,
    floatingFilter: false,
    suppressHeaderMenuButton: true,
    suppressHeaderFilterButton: true,
    suppressMovable: true,
    wrapHeaderText: false,
    autoHeaderHeight: false,
    ...defaultColDef,
    cellRenderer: defaultColDef?.cellRenderer || TruncatedText,
    headerComponent: defaultColDef?.headerComponent || CustomHeader,
  }), [defaultColDef])

  const autoHeight = rest.domLayout === 'autoHeight'

  return (
    <div
      className={`ag-grid-table ${className}${loading ? ' is-loading' : ''}`}
      style={{ height: autoHeight ? 'auto' : height, width: '100%' }}
    >
      <AgGridReact
        theme={appTheme}
        rowData={loading ? [] : rowData}
        columnDefs={columnDefs}
        defaultColDef={mergedDefaultColDef}
        getRowClass={getRowClass}
        getRowId={getRowId}
        onGridReady={onGridReady}
        animateRows
        pagination={pagination}
        paginationPageSize={Number(paginationPageSize)}
        suppressPaginationPanel={pagination}
        suppressCellFocus
        suppressMovableColumns
        suppressColumnMoveAnimation
        enableCellTextSelection
        ensureDomOrder
        headerHeight={headerHeight}
        autoHeaderHeight={false}
        {...rest}
      />
      {loading && (
        <div className="ag-grid-table-loader" aria-busy="true" aria-live="polite">
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      )}
    </div>
  )
}
